import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaInstagram, 
  FaTiktok,
  FaYoutube,
  FaApple,
  FaGooglePlay,
  FaWhatsapp,
  FaFacebook
} from 'react-icons/fa';
import { 
  MapPinIcon, 
  PhoneIcon, 
  EnvelopeIcon,
  ClockIcon 
} from '@heroicons/react/24/outline';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Facility', path: '/facility' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Shop', path: '/shop' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
    { name: 'Blog', path: '/blog' },
  ];

  const services = [
    { name: 'Gym & Fitness', path: '/services/gym' },
    { name: 'Classes & Yoga', path: '/services/yoga' },
    { name: 'Infrared Sauna', path: '/services/sauna' },
    { name: 'Steam Room', path: '/services/steam' },
  ];

  // Updated social links with correct URLs
  const socialLinks = [
    { icon: FaInstagram, href: 'https://www.instagram.com/stories/powercorefitnessgym_/', label: 'Instagram', color: 'hover:bg-pink-600' },
    { icon: FaTiktok, href: 'https://www.tiktok.com/@pcfgym', label: 'TikTok', color: 'hover:bg-neutral-700' },
    { icon: FaWhatsapp, href: 'https://wa.me/254117110000', label: 'WhatsApp', color: 'hover:bg-green-600' },
    { icon: FaFacebook, href: 'https://web.facebook.com/profile.php?id=61589568021419', label: 'Facebook', color: 'hover:bg-blue-600' },
  ];

  // Updated contact info with correct Google Maps link
  const contactInfo = [
    { icon: MapPinIcon, text: 'Southfield Mall, Nairobi, Kenya', href: 'https://share.google/kWFfHwv3VOOGAPOzR' },
    { icon: PhoneIcon, text: '0117 110 000', href: 'tel:0117110000' },
    { icon: EnvelopeIcon, text: 'powercoreltd26@gmail.com', href: 'mailto:powercoreltd26@gmail.com' },
    { icon: ClockIcon, text: 'Mon-Fri: 4am-10pm | Sat: 6am-9pm | Sun: 8am-8pm' },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-64 h-64 bg-primary rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary rounded-full filter blur-3xl"></div>
      </div>

      {/* Main Footer */}
      <div className="container-custom relative z-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* Brand Column */}
          <motion.div {...fadeInUp} className="space-y-6">
            <Link to="/" className="inline-block group">
              <span className="text-3xl font-black">
                <span className="text-primary group-hover:text-primary-light transition-colors">POWER</span>
                <span className="text-white">CORE</span>
              </span>
              <span className="block text-xs text-gray-400 -mt-1 tracking-wider">-Fitness-</span>
            </Link>
            
            <p className="text-gray-400 text-sm leading-relaxed">
              Empowering your fitness journey with state-of-the-art facilities, 
              expert trainers, and a supportive community since 2026.
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -3, scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className={`bg-gray-800 p-3 rounded-full hover:bg-primary transition-all duration-300 ${social.color}`}
                  aria-label={social.label}
                >
                  <social.icon className="h-4 w-4" />
                </motion.a>
              ))}
            </div>

            {/* App Downloads */}
            <div className="pt-4">
              <p className="text-sm font-semibold mb-3">Download our app</p>
              <div className="flex space-x-3">
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="#"
                  className="flex items-center space-x-2 bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-lg transition-colors"
                >
                  <FaApple className="h-5 w-5" />
                  <span className="text-xs">App Store</span>
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="#"
                  className="flex items-center space-x-2 bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-lg transition-colors"
                >
                  <FaGooglePlay className="h-5 w-5" />
                  <span className="text-xs">Google Play</span>
                </motion.a>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div {...fadeInUp} transition={{ duration: 0.6, delay: 0.2 }}>
            <h3 className="text-lg font-semibold mb-6 relative inline-block">
              Quick Links
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-primary"></span>
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <motion.li 
                  key={link.path}
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-primary transition-colors duration-300 text-sm flex items-center"
                  >
                    <span className="mr-2">›</span>
                    {link.name}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div {...fadeInUp} transition={{ duration: 0.6, delay: 0.3 }}>
            <h3 className="text-lg font-semibold mb-6 relative inline-block">
              Our Services
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-primary"></span>
            </h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <motion.li 
                  key={index}
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Link
                    to={service.path}
                    className="text-gray-400 hover:text-primary transition-colors duration-300 text-sm flex items-center"
                  >
                    <span className="mr-2">›</span>
                    {service.name}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div {...fadeInUp} transition={{ duration: 0.6, delay: 0.4 }}>
            <h3 className="text-lg font-semibold mb-6 relative inline-block">
              Get In Touch
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-primary"></span>
            </h3>
            <ul className="space-y-4">
              {contactInfo.map((item, index) => (
                <motion.li 
                  key={index}
                  whileHover={{ x: 5 }}
                  className="flex items-start space-x-3 group"
                >
                  <item.icon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                  {item.href ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 text-sm group-hover:text-white transition-colors hover:text-primary"
                    >
                      {item.text}
                    </a>
                  ) : (
                    <span className="text-gray-400 text-sm group-hover:text-white transition-colors">{item.text}</span>
                  )}
                </motion.li>
              ))}
            </ul>

            {/* Newsletter Signup */}
            <motion.div 
              className="mt-8"
              whileHover={{ scale: 1.02 }}
            >
              <h4 className="text-sm font-semibold mb-3">Subscribe to our newsletter</h4>
              <form className="flex" onSubmit={(e) => e.preventDefault()}>
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 px-4 py-3 bg-gray-800 text-white rounded-l-lg focus:outline-none focus:ring-1 focus:ring-primary text-sm"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit"
                  className="bg-primary px-6 py-3 rounded-r-lg hover:bg-primary-dark transition-colors duration-300 text-sm font-medium"
                >
                  Join
                </motion.button>
              </form>
              <p className="text-xs text-gray-500 mt-2">
                Get fitness tips and exclusive offers
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800 relative z-10">
        <div className="container-custom py-6">
          <div className="flex flex-col justify-center items-center text-center text-sm text-gray-400">
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              &copy; {currentYear} PowerCore Fitness. All rights reserved.
            </motion.p>
            
            {/* Website Created By Credit - Centered */}
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-2 text-xs"
            >
              Website created by{' '}
              <a 
                href="https://wa.me/254748798287?text=Hello%20Brian%20saw%20your%20PCF%20Gym%20website"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary-light transition-colors hover:underline font-medium"
              >
                iTechSolved
              </a>
            </motion.p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;